import React from 'react';
import { UserPreview, UserPreviewProps, UserPreviewSkeleton } from '@doveit/bricks';
import { AgentPreviewStatus } from '../../components/types';
import AgentAvatar from '../../components/agent-avatar/AgentAvatar';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import { toAgentPreviewProps, ToAgentPreviewPropsOptions } from '../../mappers/toAgentPreviewProps';
import { Agent } from '../../../providers/api/dtos/agent';

export type ViewAgentSmallPreviewProps = Omit<UserPreviewProps, 'children' | 'avatar'> & ({
  agentIdOrEmail: number | string,
  status?: undefined,
  needsSupervisor?: undefined,
  inTrial?: undefined,
  photoId?: undefined,
  details?: React.ReactNode | ((agent: Agent) => React.ReactNode)
  omit?: ToAgentPreviewPropsOptions['omit'],
  hideOnError?: boolean,
} | {
  agentIdOrEmail?: undefined,
  status?: AgentPreviewStatus,
  needsSupervisor?: boolean,
  inTrial?: boolean,
  photoId?: string,
  details?: React.ReactNode | ((agent: Agent) => React.ReactNode),
  omit?: undefined,
  hideOnError?: undefined
});

export const ViewAgentSmallPreview: React.FC<ViewAgentSmallPreviewProps> = ({
  agentIdOrEmail,
  status,
  name,
  size,
  needsSupervisor,
  inTrial,
  details,
  photoId,
  omit,
  hideOnError = false,
  ...rest
}) => {
  const {
    data: agent,
    isLoading: isAgentLoading,
    error: agentError,
  } = useAgent(agentIdOrEmail);

  const renderDetails = React.useCallback(
    () => {
      if (typeof details === 'function') {
        return agent ? details(agent) : undefined;
      }
      return details ?? undefined;
    },
    [details, agent],
  );

  if (!agentIdOrEmail) {
    return (
      <UserPreview
        aria-label={name ? `Anteprima di ${name}` : undefined}
        {...rest}
        size={size}
        name={name}
        renderAvatar={(avatarProps) => (
          <AgentAvatar
            {...avatarProps}
            status={status}
            needsSupervisor={needsSupervisor}
            inTrial={inTrial}
            photoId={photoId}
          />
        )}
      >
        {renderDetails()}
      </UserPreview>
    );
  }

  if (isAgentLoading) {
    return (
      <UserPreviewSkeleton
        size={size}
        aria-label="Informazioni sull'agente in caricamento"
      />
    );
  }

  if (agentError || !agent) {
    return hideOnError ? null : (
      <UserPreview
        name={omit?.name ? undefined : 'Errore agente'}
        size={size}
      />
    );
  }

  const agentPreview = toAgentPreviewProps(agent, { omit });

  return (
    <UserPreview
      aria-label={agentPreview.name ? `Anteprima di ${agentPreview.name}` : undefined}
      {...rest}
      size={size}
      name={agentPreview.name}
      renderAvatar={(avatarProps) => (
        <AgentAvatar
          {...avatarProps}
          status={agentPreview.status}
          needsSupervisor={agentPreview.needsSupervisor}
          inTrial={agentPreview.inTrial}
          photoId={agentPreview.photoId}
        />
      )}
    >
      {renderDetails()}
    </UserPreview>
  );
};

export default ViewAgentSmallPreview;
